import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Box, Button, CircularProgress, Snackbar, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Sidebar from '../Sidebar';
import AppHeader from '../AppHeader';
import ConceptDetailBody from './ConceptDetailBody';
import PolicyDetailBody from './PolicyDetailBody';
import ParameterDetailBody from './ParameterDetailBody';
import ApproveDialog from './ApproveDialog';
import SupersedeDialog from './SupersedeDialog';
import ValidationMessages, { ValidationProblem } from './ValidationMessages';
import OntologyReachRail from './OntologyReachRail';
import DocumentDetailBody from './DocumentDetailBody';
import DocumentImpactPanel from './DocumentImpactPanel';
import { ScopeLevelChip, StatusChip } from './OntologyBrowser';
import {
    DOC_KIND, KIND_LABEL, KIND_SINGULAR, OntologyKind, VALIDATE_RESOURCE, codeOf,
    formatDate, lifecycleStateOf, safeDecodeCode,
} from './ontologyFormat';
import {
    apiForKind, isKind, normalizeDocument, normalizeList, normalizeSourceDocument,
    normalizeValidation, ontologyErrorMessage, sourceDocumentsApi, validateDocument,
} from '../../api/ontologyApi';
import { dsVar, ink } from '../../theme/tokens';

/**
 * The page splits into WHAT THIS MEANS and WHAT THIS REACHES.
 *
 * The left column is a reading flow — definition, guidance, how it is measured, how it is
 * tuned. The right rail is the blast radius: the thresholds configured against the
 * record, the terms beside it, the agents running it. Those are reference blocks a reader
 * consults WHILE reading the tuning guidance that raises the question, not chapters to
 * scroll past afterwards; stacked at the bottom they were a screen away from the text
 * that motivates them, and for the 154-of-270 concepts no template cites, the payoff for
 * that scroll was a single line of text saying so.
 *
 * OntologyReachRail owns which sections qualify and why. This file owns only the geometry
 * — and the geometry has one rule: the split engages only where both columns genuinely
 * fit. Below the breakpoint the rail returns to the bottom of the flow, which is the
 * layout the page has always had, so nothing is lost on a narrow screen.
 */
const READING_COLUMN = '52rem';
const RAIL_WIDTH = '22rem';

/** Reading column + rail + gutters, with the 256px sidebar and page padding accounted for. */
const WIDE = '@media (min-width: 1280px)';

/**
 * One ontology document, with everything that can legitimately be done to it.
 *
 * The lifecycle is the substance of this screen, and it is not a preference:
 *
 *   - a system/regulatory document is shared across tenants and cannot be edited at all.
 *     The only move is CUSTOMISE, which clones it as an organisation draft;
 *   - an organisation draft can be edited, deleted or approved — a draft is invisible to
 *     the engine, which is exactly what makes editing it safe;
 *   - an organisation approved document can only be SUPERSEDED. It may already have
 *     governed recorded decisions, so changing it in place would rewrite history.
 *
 * Actions that are unavailable are rendered DISABLED with the reason, not hidden. Hiding
 * them makes the rule invisible, and a user who does not know why they cannot edit will
 * assume the screen is broken.
 */
const OntologyDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams<{ kind?: string; code?: string }>();

    /**
     * Source documents share this route but are NOT a kind.
     *
     * They are read-only: no approve, no supersede, no delete, and no /validate resource.
     * Everything below that is keyed on `kind` — apiForKind, lifecycleStateOf, the version
     * list, the validation pass — is therefore skipped for one, and the reading column and
     * rail are swapped for the document body and its impact panel.
     */
    const isDocument = params.kind === DOC_KIND;
    const kind = (isKind(params.kind) ? params.kind : 'concepts') as OntologyKind;
    const code = safeDecodeCode(params.code);

    const [document, setDocument] = useState<Record<string, any> | null>(null);
    const [versions, setVersions] = useState<Record<string, any>[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const [warnings, setWarnings] = useState<ValidationProblem[]>([]);
    const [approveOpen, setApproveOpen] = useState(false);
    const [supersedeOpen, setSupersedeOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const api = isDocument ? null : apiForKind(kind);

    const load = useCallback(async (signal?: AbortSignal) => {
        if (!code) return;

        if (isDocument) {
            setLoading(true);
            setError(null);
            try {
                const res = await sourceDocumentsApi.get(code, { signal });
                setDocument(normalizeSourceDocument(res));
                // The registry is versioned on the same key as everything else, but this
                // API exposes no way to list those versions, so the panel stays empty
                // rather than showing a list of one.
                setVersions([]);
            } catch (err: any) {
                if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
                console.error(`Error loading source document ${code}:`, err);
                setError(ontologyErrorMessage(err, 'Could not load this document.'));
            } finally {
                setLoading(false);
            }
            return;
        }

        if (!api) return;
        setLoading(true);
        setError(null);
        try {
            // Two reads: the document the engine would pick, and every version of the
            // code. getByCode returns only the winner, so without the second the screen
            // could not show that a draft and an approved version both exist.
            const [detailRes, versionsRes] = await Promise.all([
                api.get(code, { signal }),
                api.list({ code, limit: 100 }, { signal }),
            ]);
            setDocument(normalizeDocument(detailRes, kind));
            setVersions(normalizeList(versionsRes, kind).documents);
        } catch (err: any) {
            if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
            console.error(`Error loading ${kind} ${code}:`, err);
            setError(ontologyErrorMessage(err, `Could not load this ${KIND_SINGULAR[kind]}.`));
        } finally {
            setLoading(false);
        }
    }, [api, code, kind, isDocument]);

    useEffect(() => {
        const controller = new AbortController();
        load(controller.signal);
        return () => controller.abort();
    }, [load]);

    // Validate on load so a warning — "your threshold is looser than statute" — is on
    // screen before the user reaches for Approve, not after.
    useEffect(() => {
        // /validate takes a concept, policy or parameter. There is no source_document
        // resource on it, and nothing to warn about on a record nobody can edit.
        if (!document || isDocument) return undefined;
        const controller = new AbortController();
        (async () => {
            try {
                const res = await validateDocument(
                    VALIDATE_RESOURCE[kind], document, { signal: controller.signal });
                setWarnings(normalizeValidation(res).warnings);
            } catch {
                // A validation outage must not stop the document being read.
                setWarnings([]);
            }
        })();
        return () => controller.abort();
    }, [document, kind, isDocument]);

    // Never a lifecycle for a read-only document: no state means no action buttons.
    const state = isDocument ? null : lifecycleStateOf(document);
    const approvedSibling = versions.find(
        (v) => v.status === 'approved' && v.scope_level === 'organisation');

    /**
     * Run a lifecycle action, reporting whatever the control plane said on failure.
     *
     * `api` is resolved from a validated `kind`, so it is always present by the time a
     * button exists to call this — but the guard keeps that a fact rather than an
     * assertion, and it is the only place the actions could be reached with none.
     */
    const runAction = async (
        action: (resourceApi: NonNullable<typeof api>) => Promise<unknown>,
        successMessage: string,
        after?: () => void,
    ) => {
        if (!api) return;
        setSubmitting(true);
        setError(null);
        try {
            await action(api);
            setNotice(successMessage);
            if (after) after();
            else await load();
        } catch (err) {
            setError(ontologyErrorMessage(err, 'That action could not be completed.'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleCustomise = () => runAction(
        async (resourceApi) => {
            const res = await resourceApi.customise(code);
            const draft = normalizeDocument(res, kind);
            // The clone is returned, not saved — posting it back is what creates the
            // draft. Sending it WHOLE is essential: an org document does not inherit
            // anything from the platform version, so any field dropped here is gone.
            return resourceApi.create(draft);
        },
        'Created an organisation draft from the platform version.',
        () => load(),
    );

    const handleApprove = (effectiveFrom: string | null) => runAction(
        (resourceApi) => resourceApi.approve(
            code, effectiveFrom ? { effective_from: effectiveFrom } : {}),
        'Approved. The engine will pick this up on its next run.',
        () => { setApproveOpen(false); load(); },
    );

    const handleSupersede = (effectiveFrom: string) => runAction(
        (resourceApi) => resourceApi.supersede(code, { effective_from: effectiveFrom }),
        'Successor draft created. Approve it to put it in force.',
        () => { setSupersedeOpen(false); load(); },
    );

    const handleDelete = () => runAction(
        (resourceApi) => resourceApi.remove(code),
        'Draft deleted.',
        () => navigate(`/ontology/${kind}`),
    );

    const title = document?.preferred_label || document?.title || code;

    return (
        <div className="flex h-screen overflow-hidden bg-page font-sans text-fg">
            <Sidebar activePage="ontology" />

            <div className="flex flex-1 flex-col overflow-hidden">
                <AppHeader
                    left={(
                        <Button
                            onClick={() => navigate(`/ontology/${isDocument ? DOC_KIND : kind}`)}
                            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                            sx={{ color: dsVar('fg-2'), textTransform: 'none', fontSize: '0.875rem' }}
                        >
                            Back to {isDocument ? 'Documents' : KIND_LABEL[kind]}
                        </Button>
                    )}
                />

                <main className="flex-1 overflow-y-auto px-8 pb-16">
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
                            <CircularProgress sx={{ color: dsVar('accent-fg') }} />
                        </Box>
                    )}

                    {!loading && error && !document && (
                        <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
                    )}

                    {!loading && document && (
                        <Box sx={{ maxWidth: '78rem', pt: 4 }}>
                            <Box sx={{ maxWidth: READING_COLUMN }}>
                                {error && (
                                    <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                                        {error}
                                    </Alert>
                                )}

                                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                    <ScopeLevelChip scopeLevel={document.scope_level} />
                                    <StatusChip status={document.status} />
                                </Box>

                                <Typography
                                    component="h1"
                                    sx={{ fontSize: '1.5rem', fontWeight: 600, color: dsVar('fg'), lineHeight: 1.3 }}
                                >
                                    {title}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontSize: '0.8125rem', color: dsVar('fg-muted'), mt: 0.5,
                                    }}
                                >
                                    {code}
                                </Typography>

                                {/* ── Lifecycle actions ── */}
                                <Box sx={{
                                    display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center',
                                    mt: 2.5, pt: 2.5, borderTop: `1px solid ${dsVar('line')}`,
                                }}>
                                    {state === 'global' && (
                                        <>
                                            <Button
                                                onClick={handleCustomise}
                                                disabled={submitting}
                                                sx={{
                                                    textTransform: 'none', fontWeight: 600,
                                                    backgroundColor: dsVar('accent'), color: dsVar('on-accent'),
                                                    '&:hover': { backgroundColor: '#F5D149' },
                                                    '&.Mui-disabled': { backgroundColor: dsVar('line'), color: dsVar('fg-muted') },
                                                }}
                                            >
                                                Customise for your organisation
                                            </Button>
                                            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted') }}>
                                                This is a shared {document.scope_level} record and cannot be
                                                edited directly.
                                            </Typography>
                                        </>
                                    )}

                                    {state === 'org_draft' && (
                                        <>
                                            <Button
                                                onClick={() => setApproveOpen(true)}
                                                disabled={submitting}
                                                sx={{
                                                    textTransform: 'none', fontWeight: 600,
                                                    backgroundColor: dsVar('accent'), color: dsVar('on-accent'),
                                                    '&:hover': { backgroundColor: '#F5D149' },
                                                }}
                                            >
                                                Approve
                                            </Button>
                                            <Button
                                                onClick={handleDelete}
                                                disabled={submitting}
                                                sx={{ textTransform: 'none', color: ink('#F87171') }}
                                            >
                                                Delete draft
                                            </Button>
                                        </>
                                    )}

                                    {state === 'org_approved' && (
                                        <>
                                            <Button
                                                onClick={() => setSupersedeOpen(true)}
                                                disabled={submitting}
                                                sx={{
                                                    textTransform: 'none', fontWeight: 600,
                                                    backgroundColor: dsVar('accent'), color: dsVar('on-accent'),
                                                    '&:hover': { backgroundColor: '#F5D149' },
                                                }}
                                            >
                                                Supersede
                                            </Button>
                                            {/* Shown disabled rather than hidden: the rule is the
                                                point, and a missing button explains nothing. */}
                                            <Tooltip title="Approved documents are superseded, not edited — an approved value may already have governed recorded decisions.">
                                                <span>
                                                    <Button disabled sx={{ textTransform: 'none', color: dsVar('fg-muted') }}>
                                                        Edit
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </>
                                    )}
                                </Box>

                                {warnings.length > 0 && (
                                    <Box sx={{ mt: 2.5 }}>
                                        <ValidationMessages warnings={warnings} />
                                    </Box>
                                )}
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex', flexDirection: 'column',
                                    alignItems: 'stretch', gap: 3,
                                    [WIDE]: { flexDirection: 'row', alignItems: 'flex-start', gap: 5 },
                                }}
                            >
                                <Box sx={{ minWidth: 0, maxWidth: READING_COLUMN, [WIDE]: { flex: 1 } }}>
                                    {/* ── Body ── */}
                                    <Box sx={{ mt: 3 }}>
                                        {isDocument && <DocumentDetailBody document={document} />}
                                        {!isDocument && kind === 'concepts' && <ConceptDetailBody document={document} />}
                                        {!isDocument && kind === 'policies' && <PolicyDetailBody document={document} />}
                                        {!isDocument && kind === 'parameters' && <ParameterDetailBody document={document} />}
                                    </Box>

                                    {/* ── Versions ── */}
                                    {versions.length > 1 && (
                                        <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${dsVar('line')}` }}>
                                            <Typography
                                                sx={{
                                                    fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em',
                                                    textTransform: 'uppercase', color: dsVar('fg-muted'), mb: 1.25,
                                                }}
                                            >
                                                Versions of this code
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {versions.map((version, index) => (
                                                    <Box
                                                        key={`${version.status}-${version.effective_from}-${index}`}
                                                        sx={{
                                                            display: 'flex', alignItems: 'center', gap: 1.5,
                                                            p: 1.25, borderRadius: '6px',
                                                            border: `1px solid ${dsVar('line')}`, backgroundColor: dsVar('surface'),
                                                        }}
                                                    >
                                                        <ScopeLevelChip scopeLevel={version.scope_level} />
                                                        <StatusChip status={version.status} />
                                                        <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-2') }}>
                                                            {version.effective_from
                                                                ? `In force from ${formatDate(version.effective_from)}`
                                                                : 'No effective date'}
                                                            {version.effective_to && ` until ${formatDate(version.effective_to)}`}
                                                        </Typography>
                                                        {version.approved_by && (
                                                            <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted'), ml: 'auto' }}>
                                                                Approved by {version.approved_by}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>

                                {/* Sticky so the blast radius stays answerable while the
                                    reader scrolls the guidance, and independently scrollable
                                    when it outruns the viewport. The first-child margin reset
                                    only applies once the rail is beside the column — stacked,
                                    it needs the gap. */}
                                <Box
                                    component="aside"
                                    sx={{
                                        [WIDE]: {
                                            position: 'sticky', top: 0,
                                            width: RAIL_WIDTH, flexShrink: 0,
                                            borderLeft: `1px solid ${dsVar('line')}`, pl: 3, pt: 3,
                                            maxHeight: '100vh', overflowY: 'auto',
                                            '& > *:first-of-type': { mt: 0 },
                                        },
                                    }}
                                >
                                    {isDocument ? (
                                        /* The document's blast radius. Same slot, same
                                           question the rail answers for the other three. */
                                        <DocumentImpactPanel code={document.source_doc_code || code} />
                                    ) : (
                                        <OntologyReachRail
                                            kind={kind}
                                            code={codeOf(document, kind) || code}
                                            document={document}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </main>
            </div>

            <ApproveDialog
                open={approveOpen}
                kind={kind}
                document={document}
                currentInForce={approvedSibling}
                warnings={warnings}
                submitting={submitting}
                onClose={() => setApproveOpen(false)}
                onConfirm={handleApprove}
            />

            <SupersedeDialog
                open={supersedeOpen}
                code={code}
                submitting={submitting}
                onClose={() => setSupersedeOpen(false)}
                onConfirm={handleSupersede}
            />

            <Snackbar
                open={Boolean(notice)}
                autoHideDuration={6000}
                onClose={() => setNotice(null)}
                message={notice}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </div>
    );
};

export default OntologyDetailPage;
